import Alert from "@mui/material/Alert";
import Logger from "../../../services/Logger";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DatamuseService from "../../../services/DatamuseService";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import StepFooter from "../../pzl_common/board/StepFooter";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import BorderHeader from "../../pzl_common/BorderHeader";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import Step3DetailsSection from "../../pzl_common/board/Step3DetailsSection";
import BorderLeftPanel from "../../pzl_common/BorderLeftPanel";
import { AdminStoryPreviewTrigger } from "../../pzl_common/board/AdminStoryPreviewTrigger";
import AdminCrossBoardService, { READY_TO_REVIEW } from "./AdminCrossBoardService";
import { usePuzzleData } from "./usePuzzleData";
import { BuggleChartTrigger } from "../../../pages/components/ClusteredBuggleChartDialog";

export default function Step3Reviewed() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [refresh, setRefresh] = useState(0);
    const { crossTpl: puzzle, loading, error: fetchError } = usePuzzleData(id, refresh);
    const [error, setError] = useState(null);
    const [publishComment, setPublishComment] = useState("");

    // Loading states
    const [loadingGoBackReadyToReview, setLoadingGoBackReadyToReview] = useState(false);
    const [loadingPublish, setLoadingPublish] = useState(false);
    const [loadingDiscard, setLoadingDiscard] = useState(false);

    // Confirmation dialog state for discarding template
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    // Confirmation dialog state for publishing
    const [openPublishDialog, setOpenPublishDialog] = useState(false);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Initialize publishComment from puzzle data
    useEffect(() => {
        if (puzzle) {
            setPublishComment(puzzle.comment || "");
        }
    }, [puzzle]);

    const handlePublishClick = () => {
        // Show confirmation dialog instead of immediately publishing
        setOpenPublishDialog(true);
    };

    const handlePublish = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingPublish(true);
        
        try {
            setError(null);
            // Get word list from filledCoordinates
            const wordList = puzzle.filledCoordinates ? JSON.parse(puzzle.filledCoordinates).map(item => item.text) : [];
            const details = await DatamuseService.requestWordScores(wordList);
            const result = await AdminCrossBoardService.publishCrossTpl(
                puzzle.id,
                publishComment,
                details,
                puzzle.free || false
            );
            if (result.code && result.code === "FAILURE") {
                setError(result.message);
            } else {
                navigate(`/admin/puzzle/cross_board/published/${puzzle.id}`);
            }
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError("Failed to publish puzzle. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingPublish(false);
            setOpenPublishDialog(false);
        }
    };

    const handleSendBackToReview = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingGoBackReadyToReview(true);
        
        try {
            setError(null);
            // Call API to change state back to READY_TO_REVIEW
            await AdminCrossBoardService.updateCrossTplState(puzzle.id, READY_TO_REVIEW);
            navigate(`/admin/puzzle/cross_board/ready-to-review/${puzzle.id}`);
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError("Failed to send back to review. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingGoBackReadyToReview(false);
        }
    };

    const handleBack = () => {
        const fromStep = searchParams.get('fromStep');
        if (fromStep !== null) {
            navigate(`/admin/puzzle/cross_board?activeStep=${fromStep}`);
        } else {
            navigate("/admin/puzzle/cross_board");
        }
    };

    const handleDiscardTemplate = () => {
        // Show confirmation dialog instead of immediately discarding
        setOpenConfirmDialog(true);
    };


    // Parse puzzle grid from filledCoordinates
    const parsePuzzleGrid = () => {
        if (!puzzle?.filledCoordinates) return [];

        try {
            const filledCoordinates = JSON.parse(puzzle.filledCoordinates);
            return initializeSquare(filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filled coordinates:', error);
            return [];
        }
    };

    // Function to initialize the square array from filledCoordinates
    const initializeSquare = (filledCoordinates) => {
        const size = 12;
        const newSquare = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        if (filledCoordinates) {
            for (const coord of filledCoordinates) {
                const matchingPosition = coord.positions.find(
                    (p) => p.row === row && p.col === col
                );
                if (matchingPosition && matchingPosition.value) {
                    return matchingPosition.value.toUpperCase();
                }
            }
        }
        return "";
    };

    const handleConfirmDiscard = async (targetId) => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        setLoadingDiscard(true);
        
        try {
            setError(null);
            // Call API to mark template as discarded
            await AdminCrossBoardService.markAsDiscarded(targetId);
            // Show success message
            // Note: You might want to add a snackbar component here
            // Navigate back to puzzle list
            navigate("/admin/puzzle/cross_board");
        } catch (err) {
            Logger.error("Error discarding template:", err);
            setError("Failed to discard template. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setLoadingDiscard(false);
            setOpenConfirmDialog(false);
        }
    };


    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
                p: 3
            }}>
                <Typography variant="h6">Loading puzzle data...</Typography>
            </Box>
        );
    }

    if (fetchError || !puzzle) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="error">
                    {fetchError || "Puzzle not found"}
                </Alert>
            </Box>
        );
    }


    return (
        <Box sx={{ width: "100%" }}>
            {/* Workflow Stepper */}
            <PuzzleWorkflowStepper
                currentState={puzzle?.state}
                puzzleId={puzzle?.id}
                size="small"
                align="left"
                onStepClick={() => setRefresh(prev => prev + 1)}
                puzzleType={puzzle?.type}
            />
            
            {/* Header with back button */}
            <BorderHeader
                onBack={handleBack}
                puzzle={puzzle}
                puzzleType="crossword"
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                {/* First Column: Puzzle Information */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <BorderLeftPanel
                        puzzle={puzzle}
                        onRefresh={() => setRefresh(prev => prev + 1)}
                        puzzleType="CROSSWORD"
                    />
                </Grid>

                {/* Second Column: Words List / Word Network */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <StepPuzzleWordPanel
                        puzzle={puzzle}
                        parsePuzzleGrid={parsePuzzleGrid}
                    />
                </Grid>

                {/* Puzzle Details */}
                <Step3DetailsSection
                    puzzle={puzzle}
                    onError={setError}
                    onSuccess={(message, severity) => {
                        setSnackbarMessage(message);
                        setSnackbarSeverity(severity);
                        setSnackbarOpen(true);
                    }}
                    onRefresh={() => setRefresh(prev => prev + 1)}
                />
            </Grid>

            {/* Step Footer with Action Buttons */}
            <StepFooter
                stepType="reviewed"
                title="Reviewed"
                description="This puzzle has been reviewed and approved. Ready for publishing."
                onDiscard={handleDiscardTemplate}
                onGoBackDraft={handleSendBackToReview}
                onPublish={handlePublishClick}
                onGoBackReadyToReview={handleSendBackToReview}
                loadingGoBackReadyToReview={loadingGoBackReadyToReview}
                loadingPublish={loadingPublish}
                loadingDiscard={loadingDiscard}
                puzzle={puzzle}
                onError={setError}
                onSuccess={(message, severity) => {
                    setSnackbarMessage(message);
                    setSnackbarSeverity(severity);
                    setSnackbarOpen(true);
                }}
                afterShare={
                    puzzle?.type === "CROSSWORD" ? (
                        <>
                            <BuggleChartTrigger templateId={puzzle.id} tplKind="cross" />
                            <AdminStoryPreviewTrigger
                                puzzle={puzzle}
                                onRefresh={() => setRefresh((prev) => prev + 1)}
                            />
                        </>
                    ) : null
                }
            />

            {/* Confirmation Dialog for Discarding Template */}
            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Confirm Discard Template"
                message="Are you sure you want to discard this template? This action cannot be undone."
                targetName={`'${puzzle?.title || 'Untitled Template'}' will be discarded`}
                targetId={puzzle?.id}
                callback={handleConfirmDiscard}
            />

            {/* Confirmation Dialog for Publishing */}
            <ConfirmDialog
                open={openPublishDialog}
                setOpen={setOpenPublishDialog}
                title="Confirm Publish Puzzle"
                message="Would you like to publish it now? Don't forget to refresh the difficulty level."
                targetName={`'${puzzle?.title || 'Untitled Puzzle'}' will be published`}
                targetId={puzzle?.id}
                callback={handlePublish}
            />

            {/* Custom Snackbar */}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
}

